import { isEraroError, eraroCodesMap, boot } from '@dazn/bootstrap-lib';
import type { triggerErrorImpressionEvent } from '@dazn/fe-events';

import { BASE64_LOGOS } from '../../configuration/brand.constants';
import { BRANDS } from '../../configuration/constants';

import { brandSpecificMarkups } from './brandSpecificMarkups';
import { safeModeMarkups } from './brandSpecificMarkups/safeModeMarkups';

export interface EndOfTheWorldInput {
    error: Error;
    sendErrorEvent: typeof triggerErrorImpressionEvent;
    safeModeHomepageURL?: string;
    safeModeOn: boolean;
    brandName: boot.brand.BrandName;
}

const reportError = ({
    error,
    sendErrorEvent,
    brandName,
}: Omit<EndOfTheWorldInput, 'safeModeOn'>): void => {
    window.newrelic?.noticeError(`End of the World: ${error.message}`);

    const itemId = isEraroError(error)
        ? [error.category, error.code, error.internal].join('-')
        : [
              eraroCodesMap.category.General,
              eraroCodesMap.code.GenericError,
              eraroCodesMap.internal.GenericError,
          ].join('-');

    sendErrorEvent({
        error: {
            itemId,
            itemType: 'error',
            type: 'end of world',
            productGroup: brandName ?? 'Dazn',
        },
    });
};

const ELEMENT_ID = 'end-of-the-world';
const showEOW = (
    safeModeOn: EndOfTheWorldInput['safeModeOn'],
    safeModeHomepageURL: EndOfTheWorldInput['safeModeHomepageURL'] = '',
    brandName: EndOfTheWorldInput['brandName'] = 'dazn',
) => {
    const el = window.document.getElementById(ELEMENT_ID);
    if (el === null || window?.dazn?.native?.isNative) {
        return;
    }
    if (safeModeOn) {
        el.innerHTML = safeModeMarkups[brandName]?.replace(
            '%URL%',
            safeModeHomepageURL,
        );
    } else {
        el.innerHTML = brandSpecificMarkups[brandName];
    }
    if (brandName !== BRANDS.DAZN) {
        el.style.backgroundImage = BASE64_LOGOS[brandName];
        if (brandName === BRANDS.BINGE) {
            el.style.backgroundSize = '200px';
        }
    }
    if (el.style) {
        el.style.display = '';
    }
};

const handleKeyboard = () => {
    if (!window.dazn) {
        const message =
            '[Bootstrap] window.dazn is undefined while End of the World';
        console.error(message);
        window.newrelic?.noticeError(new Error(message));
    }
    const exitApp = window.dazn ? window.dazn.application.exitApp : undefined;
    if (exitApp === undefined) {
        return;
    }

    const KEYDOWN = window.dazn.event?.back?.KEYDOWN;
    if (KEYDOWN !== undefined) {
        window.addEventListener(KEYDOWN, exitApp);
    }

    const KEYEXIT = window.dazn.event?.exit?.KEYDOWN;
    if (KEYEXIT !== undefined) {
        window.addEventListener(KEYEXIT, exitApp);
    }
};

export const displayEndOfTheWorldError = ({
    error,
    sendErrorEvent,
    safeModeHomepageURL,
    safeModeOn,
    brandName,
}: EndOfTheWorldInput) => {
    if (error) {
        console.error(error);
    }

    reportError({
        error,
        sendErrorEvent,
        brandName,
    });

    showEOW(safeModeOn, safeModeHomepageURL, brandName);
    handleKeyboard();
};
